import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ShareProductModal from "./components/ShareProductModal";
import PostCard from "./components/PostCard";
import CreatePostPanel from "./components/CreatePostPanel";
import SocialLeftSidebar from "./components/SocialLeftSidebar";
import SocialRightSidebar from "./components/SocialRightSidebar";
import { mockPosts } from "@/mocks/social";
import SEO from "@/components/base/SEO";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const socialSchema = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "StarBookPrime Social — Share Life & Earn Commission",
    "description": "Join StarBookPrime Social — like Facebook, share your life photos and moments. Plus earn real commissions by sharing verified factory products to your YouTube, TikTok, and Instagram fans.",
    "url": `${SITE_URL}/social`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Social", "item": `${SITE_URL}/social` }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "StarBookPrime Social Commerce & Commission Program",
    "description": "Share life photos and earn real commissions by sharing verified factory products to your YouTube, TikTok, and Instagram fans. Up to 15% commission per sale.",
    "provider": {
      "@type": "Organization",
      "name": "StarBookPrime",
      "url": SITE_URL,
    },
    "serviceType": "Social Commerce",
    "areaServed": "Worldwide",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free to join — earn up to 15% commission per sale via PayPal",
    },
  },
];

const FILTERS = ["All", "Life Photos", "Product Shares", "YouTube", "TikTok", "Instagram"];

interface ExtraPost {
  id: string;
  user: string;
  username: string;
  avatar: string;
  role: string;
  platform: string;
  followers: string | null;
  content: string;
  image: string;
  likes: number;
  comments: number;
  shares: number;
  time: string;
  tagged: string[];
  type: string;
  earned: string | null;
  tag: string | null;
}

export default function SocialPage() {
  const [showShareModal, setShowShareModal] = useState(false);
  const [extraPosts, setExtraPosts] = useState<ExtraPost[]>([]);
  const [activeFilter, setActiveFilter] = useState("All");

  const handleProductShare = (post: {
    content: string;
    productId: string;
    productTitle: string;
    productImage: string;
    referralLink: string;
  }) => {
    const newPost: ExtraPost = {
      id: `shared_${Date.now()}`,
      user: "You",
      username: "@myhandle",
      avatar: "https://public.readdy.ai/ai/img_res/293c8f1f997b2bd76ecae12a34f423e2.jpg",
      time: "Just now",
      content: post.content,
      image: post.productImage,
      likes: 0,
      comments: 0,
      shares: 0,
      role: "influencer",
      platform: "tiktok",
      followers: null,
      tagged: [post.productId],
      type: "product",
      earned: null,
      tag: "Product",
    };
    setExtraPosts((prev) => [newPost, ...prev]);
  };

  const allPosts = [...extraPosts, ...mockPosts];

  const filteredPosts = allPosts.filter((p) => {
    if (activeFilter === "All") return true;
    if (activeFilter === "Life Photos") return p.type === "life";
    if (activeFilter === "Product Shares") return p.type === "product";
    if (activeFilter === "YouTube") return p.platform === "youtube";
    if (activeFilter === "TikTok") return p.platform === "tiktok";
    if (activeFilter === "Instagram") return p.platform === "instagram";
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="StarBookPrime Social — Share Life & Earn Commission | StarBookPrime"
        description="Join StarBookPrime Social — like Facebook, share your life photos and moments. Plus earn real commissions by sharing verified factory products to your YouTube, TikTok, and Instagram fans."
        keywords="StarBookPrime social, share life photos, earn commission, verified factory products, TikTok YouTube Instagram"
        canonical="/social"
        schema={socialSchema}
      />
      <Navbar />

      {showShareModal && (
        <ShareProductModal
          onClose={() => setShowShareModal(false)}
          onShare={handleProductShare}
        />
      )}

      {/* Hero Banner */}
      <div
        className="pt-16 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #B8860B 0%, #DAA520 40%, #FFD700 70%, #DAA520 100%)" }}
      >
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20gold%20sparkle%20light%20rays%20celebration%20background%20minimal%20elegant%20warm&width=1440&height=300&seq=social-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-950/20 text-amber-950 text-xs font-bold uppercase tracking-widest mb-4">
            <i className="ri-star-fill text-yellow-600" />
            StarBookPrime Social
            <i className="ri-star-fill text-yellow-600" />
          </div>
          <h1
            className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-amber-950 mb-3 leading-tight"
            style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
          >
            Share Your Life.{" "}
            <span className="text-amber-800">Earn Real Money.</span>
          </h1>
          <p
            className="text-base md:text-lg text-amber-900 max-w-3xl mx-auto font-semibold leading-relaxed"
            style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
          >
            Welcome everyone — like Facebook, share your life photos and moments with the world.
            Plus earn commissions by sharing verified factory products to your{" "}
            <strong>YouTube</strong>, <strong>TikTok</strong>, and <strong>Instagram</strong> fans.
            Together, sharing more means earning more — for your family&apos;s better life.
          </p>

          {/* Quick Stats */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-6">
            {[
              { icon: "ri-user-heart-line", value: "180K+", label: "Community Members" },
              { icon: "ri-money-dollar-circle-line", value: "$2.4M+", label: "Commissions Paid" },
              { icon: "ri-store-2-line", value: "1M+", label: "Verified Products" },
              { icon: "ri-percent-line", value: "Up to 15%", label: "Commission Rate" },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-2 text-amber-950">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-950/10">
                  <i className={`${stat.icon} text-sm`} />
                </div>
                <div className="text-left">
                  <div className="text-sm font-extrabold">{stat.value}</div>
                  <div className="text-xs text-amber-800">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border-b border-gray-100 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  activeFilter === f
                    ? "bg-orange-500 text-white"
                    : "text-gray-600 hover:bg-orange-50 hover:text-orange-500"
                }`}
              >
                {f}
              </button>
            ))}
            <div className="ml-auto flex-shrink-0">
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white text-sm font-bold rounded-full hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-money-dollar-circle-line" /> Share & Earn
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex gap-6">
        <SocialLeftSidebar onShareProduct={() => setShowShareModal(true)} />

        {/* Feed */}
        <main className="flex-1 min-w-0">
          <CreatePostPanel onShareProduct={() => setShowShareModal(true)} />

          {filteredPosts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-orange-50 mx-auto mb-4">
                <i className="ri-image-line text-3xl text-orange-300" />
              </div>
              <p className="text-gray-500 text-sm">No posts in this category yet. Be the first to share!</p>
            </div>
          ) : (
            filteredPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onShareProduct={() => setShowShareModal(true)}
              />
            ))
          )}
        </main>

        <SocialRightSidebar onShareProduct={() => setShowShareModal(true)} />
      </div>

      <Footer />
    </div>
  );
}
